import { useState, useEffect } from 'react';
import axios from 'axios';
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from '../utils/firebase-config';

const useConstantContactService = (userId, email, firstName, lastName) => {
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);

  // Function to refresh the access token if it expires
  const refreshAccessToken = async () => {
    try {
      console.log('Refreshing access token...');
      const response = await axios.post(
        'https://authz.constantcontact.com/oauth2/default/v1/token',
        new URLSearchParams({
          refresh_token: refreshToken,
          client_id: process.env.REACT_APP_CONSTANT_CONTACT_CLIENT_ID,
          client_secret: process.env.REACT_APP_CONSTANT_CONTACT_CLIENT_SECRET,
          grant_type: 'refresh_token',
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );

      const newAccessToken = response.data.access_token;
      const newRefreshToken = response.data.refresh_token || refreshToken;

      console.log('Access token refreshed:', newAccessToken);

      // Store new tokens in Firestore
      await storeTokensForUser(userId, newAccessToken, newRefreshToken);

      setAccessToken(newAccessToken);
      setRefreshToken(newRefreshToken);

      return { accessToken: newAccessToken, refreshToken: newRefreshToken };
    } catch (error) {
      console.error('Error refreshing access token:', error);
      throw error;
    }
  };

  // Function to store new access and refresh tokens in the database
  const storeTokensForUser = async (userId, accessToken, refreshToken) => {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, { accessToken, refreshToken }, { merge: true });
    console.log('Tokens stored for user:', userId);
  };

  // Function to get user tokens from the database
  const getTokensForUser = async () => {
    const userRef = doc(db, 'users', userId);
    const userSnapshot = await getDoc(userRef);
    if (userSnapshot.exists()) {
      const { accessToken, refreshToken } = userSnapshot.data();
      setAccessToken(accessToken);
      setRefreshToken(refreshToken);
      console.log('Tokens retrieved for user:', userId);
    } else {
      throw new Error('User tokens not found');
    }
  };

  // Function to add user to Constant Contact list
  const addUserToList = async () => {
    try {
      console.log('Fetching user tokens...');
      await getTokensForUser();

      console.log('Adding user to Constant Contact:', { email, firstName, lastName });
      let response = await axios.post(
        'https://api.cc.email/v3/contacts/sign_up_form',
        {
          email_address: email,
          first_name: firstName,
          last_name: lastName,
          list_ids: ['your-list-id'], // Replace with your Constant Contact list ID
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`, // Use the stored access token
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 202) {
        console.log('Contact added successfully:', response.data);
      } else {
        console.log('Unexpected status code:', response.status);
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        console.log('Access token expired, refreshing...');
        const newTokens = await refreshAccessToken();

        // Retry with the new token
        console.log('Retrying with new access token:', newTokens.accessToken);
        const retryResponse = await axios.post(
          'https://api.cc.email/v3/contacts/sign_up_form',
          {
            email_address: email,
            first_name: firstName,
            last_name: lastName,
            list_ids: ['your-list-id'], // Replace with your Constant Contact list ID
          },
          {
            headers: {
              Authorization: `Bearer ${newTokens.accessToken}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (retryResponse.status === 202) {
          console.log('Contact added successfully after token refresh:', retryResponse.data);
        }
      } else {
        console.error('Error adding contact to Constant Contact:', error.response || error);
      }
    }
  };

  // Automatically add user when `userId` changes and is valid
  useEffect(() => {
    if (userId && email && firstName && lastName) {
      console.log('Attempting to add user to Constant Contact...', { userId, email, firstName, lastName });
      addUserToList();  // Add user to Constant Contact automatically when `userId` changes
    } else {
      console.log('Waiting for required information before adding user...');
    }
  }, [userId, email, firstName, lastName]);
};

export default useConstantContactService;
